// Domain model for the OPG CoPilot (CR-20260710-001, phase 1: shell + mock).
//
// Frontend-owned: NOTHING here persists server-side; the session is transient and
// this-client-only (spec §9). ISOLATED SIBLING: re-implements the Hedge/APG CoPilots'
// proven shapes WITHOUT importing them (family rule — duplication, never cross-folder
// imports).
//
// TWO sanctioned exceptions live in copilot-shared/, each owner-ruled individually:
//   expiration-dates.ts     the tradeable-date rule        (2026-07-08)
//   copilot-disclaimer.ts   the compliance disclaimer text (2026-07-20, CR-20260720-001 R8)
// The test both passed: "is duplicating this ever CORRECT?" For a SHAPE or logic, yes — that is
// the whole point of the family rule, letting each Co-Pilot evolve alone. For a single correctness
// fact that must read identically everywhere, no — there duplication is only drift risk.
//
// Vocabulary traps (spec §2):
//   - CoPilot trades are NOT OpgTemplates (the panel's saved-template feature): never
//     populate descriptor.templateId/templateName.
//   - The "starting strike" is NOT part of a strategy descriptor — legs are strike-less
//     molds; the starting strike is where the user would click / where the X border points.
//
// ES5 note: arrays + indexOf only (no Set/Map iteration, no for..of), matching the
// sibling copilot logic.

import {StrategyTemplate} from '../options-pricing-grid/model/strategy.template';
import {PricingGridLegDescriptor} from '../options-pricing-grid/model/pricing-grid-leg.descriptor';

export type TabKind = 'calls' | 'puts' | 'callsAndPuts';

export type OpgSide = 'calls' | 'puts';

// ---- Questionnaire inputs (spec §5) ----------------------------------------------------

// Date selection — the panel's currently-loaded date, a single tradeable date, or a
// tradeable-date range. Tradeable = a listed option-chain expiration (copilot-shared/
// expiration-dates.ts is the one rule).
//   - 'loaded' (WI-3, amendment A5): a UI-STATE-ONLY variant meaning "use whatever expiration
//     the panel currently shows". It carries NO date of its own; the orchestrator resolves it
//     to {kind:'single', date:<panel's selected expiration AT RUN TIME>} when it builds the
//     skill request, re-reading the facade at Run (never cached at dialog open). The wire the
//     skill sees is therefore always single|range — 'loaded' never leaves the frontend (courier).
export type OpgDateSelection =
    | { kind: 'loaded' }
    | { kind: 'single'; date: Date }
    | { kind: 'range'; start: Date; end: Date };

// The atmOffset anchor (A3, owner smoke 2026-07-10): 'chosen' = the OPG's chosen/override
// ATM (centerStrike, the black cell); 'market' = the live market ATM (the white cell).
// An EXPLICIT user input — supersedes "chosen-vs-dynamic surfacing is Chris's deferred
// call" (old spec open #3).
export type OpgAtmAnchor = 'chosen' | 'market';

// How a bounded Distance-To-Cover choice sizes its range (Both CR, vrequest 2026-07-15):
// 'dollar' = a static extent the user types (positive, strike-step multiple); 'expectedRange'
// = dynamic — the per-date range END is the SKILL's own calculation (the CR-20260715-001
// owner ruling), so the frontend couriers only the fact and carries NO number. Exclusive
// choice: the UI is one $ box plus a "Use Expected Range" checkbox that disables it.
export type OpgAtmRangeValue =
    | { mode: 'dollar'; extent: number }
    | { mode: 'expectedRange' };

// The SECONDARY "off that strike" choice under ATM Offset (WI-4): a bounded Range (sized by
// an OpgAtmRangeValue, swept in the side's OTM direction), an open-ended Follow-Through (no
// further input — runs to the OTM ladder end), or Both (Both CR — the UNION of the two: the
// skill returns range AND follow-through candidates off the same offset start; carries the
// same range value the Range choice needs, because the range family still must be sized).
// Supersedes CR-20260715-001's `{kind:'expectedRange'}` third choice: the expected range is
// not a coverage SHAPE, it is the dynamic way of sizing a range — it lives in the value now.
export type OpgAtmOffsetSub =
    | { kind: 'range'; value: OpgAtmRangeValue }
    | { kind: 'followThrough' }
    | { kind: 'both'; value: OpgAtmRangeValue };

// Price-range form — EXACTLY ONE variant. 'atmOffset' is NEW vs the Hedge CoPilot (§5).
// WI-4 (owner meeting 2) restructured it: ATM Offset is the PRIMARY choice; `offset` is ONE
// SIGNED dollar value that sets where the region STARTS off the selected `anchor` ATM — the
// grid's own Call/Put Offset protocol (pricing-grid.row.ts): negative = toward/into the money,
// positive = further out-of-the-money, and the call/put side resolves the actual direction.
// `sub` is the SECONDARY choice — a bounded Range, an open-ended Follow-Through, or the
// Expected Range off that start. (Supersedes A3's `up`/`down` pair, which wrongly made ATM
// Offset itself a range.)
//
// 'expectedRange' (CR-20260715-001): the user gives a START STRIKE and NOTHING else. There
// is deliberately NO direction member — calls can only cover UP to the expected range, puts
// only DOWN (the side implies it; offering a control would let users express impossible
// coverage). There is NO end/value member either: the per-date range end is calculated by
// the SKILL (owner ruling 2026-07-15 — the frontend passes only the fact, pure courier).
//
// 'followThrough' (CR-20260727-002): a START STRIKE and nothing else, for the SAME reason —
// calls only run up, puts only down. It used to carry `direction: 'up' | 'down'` fed by an
// Up | Down selector, which let a puts side declare it ran upward; the backend then ignored
// the field and derived the direction from the side anyway (OpgRangeReducer.cs:105-107), so
// the control's only effect was to let the user state something untrue. Both the member and
// the selector are gone; the questionnaire STATES the coverage instead of asking for it.
export type OpgPriceRange =
    | { kind: 'strikes'; startStrike: number; endStrike: number }
    | { kind: 'followThrough'; startStrike: number }
    | { kind: 'atmOffset'; anchor: OpgAtmAnchor; offset: number; sub: OpgAtmOffsetSub }
    | { kind: 'expectedRange'; startStrike: number };

// Is this side's coverage PURELY follow-through — the main mode, or ATM Offset with the
// Follow-Through sub? Such a side has an accelerator and NO brake, and risk levels size the
// brake, so the Max Risk question has no answer and must not be asked: the fieldset hides,
// the side completes with maxRisk == null, and the wire carries the null
// (hide-risk-for-follow-through, meeting 2026-07-15 pkg-03, owner 00:43–00:44).
// Deliberately "Only": the 'both' sub INCLUDES follow-throughs but keeps the range family,
// so the question survives there (risk shapes the ranges — owner 00:28); 'expectedRange' is
// a range-family mode. Null/malformed input → false: fail toward SHOWING the question.
// ONE predicate drives the UI visibility, the Run gate and the write guard, so they can
// never disagree (the WI-7 rule).
export function isFollowThroughOnly(pr: OpgPriceRange): boolean {
    if (pr == null) {
        return false;
    }
    if (pr.kind === 'followThrough') {
        return true;
    }
    return pr.kind === 'atmOffset' && pr.sub != null && pr.sub.kind === 'followThrough';
}

// FOUR relative risk levels — 'All' is OPG-only and is NOT the Hedge 'Auto' (§5, §10:
// never render High/Conservative/Auto in this dialog).
export type OpgRelativeRiskLevel = 'Low' | 'Moderate' | 'Aggressive' | 'All';
export const OPG_RELATIVE_RISK_LEVELS: OpgRelativeRiskLevel[] = ['Low', 'Moderate', 'Aggressive', 'All'];

export type OpgMaxRisk =
    // normalized = the entered amount is a NORMALIZED value, not plain dollars (family
    // discriminator; the skill interprets the number).
    | { kind: 'dollar'; amount: number; normalized: boolean }
    | { kind: 'relative'; level: OpgRelativeRiskLevel };

export interface SideInputs {
    mode?: 'custom' | 'preset';
    presetId?: string | null;
    presetApgSource?: string | null;
    presetValid?: boolean;
    presetName?: string | null;
    presetAnchor?: number | null;
    priceRange: OpgPriceRange;
    minProfit: { amount: number; normalized: boolean };
    // null = the Max Risk question DOES NOT EXIST for this side — a follow-through-only
    // coverage (isFollowThroughOnly) has no brake for a risk level to size, so the pad never
    // asks and the wire carries the absence (hide-risk-for-follow-through, meeting 2026-07-15
    // pkg-03). Never "unanswered": an unanswered risk on a range-family side is a NON-null
    // {kind:'dollar', amount: NaN}-style value that fails isOpgMaxRiskValid. strictNullChecks
    // is off, so this union is the documented contract, not a compiler guarantee.
    maxRisk: OpgMaxRisk | null;
}

// Calls tab / Puts tab: one date + one side's inputs.
export interface SingleSideTabInputs {
    date: OpgDateSelection;
    side: SideInputs;
}

// Calls & Puts tab: the date is SHARED; both sides' inputs are required — a collapsed
// side is a VISUAL state only, its inputs still gate the Run (§5).
export interface DualSideTabInputs {
    date: OpgDateSelection;
    calls: SideInputs;
    puts: SideInputs;
}

export type TabInputs = SingleSideTabInputs | DualSideTabInputs;

export function isDualSideInputs(i: TabInputs): i is DualSideTabInputs {
    return i != null && (i as DualSideTabInputs).calls !== undefined;
}

// ---- Market-condition overrides (CR-20260722-002) ----------------------------------------

// User-supplied market conditions for ONE date, replacing what the skill would measure.
//
// SHAPE: EVERY value is per side (CR-20260921-002, client ruling 2026-09-21, card LbJt5NTE,
// REPLACING owner ruling D5). The market reads ONE IV and ONE HS per expiration — the seed writes
// the same reading into both sides — but the trader may override each side on its own: "I'm
// trying to evoke situations and combinations here and see the difference of them for calls and
// puts." Editing the Calls fieldset never moves the Puts one, exactly as VS has always behaved.
// vsCall/vsPut were per side from the start: VerticalSkewService scans each side separately.
//
// NOT part of TabInputs, deliberately. Overrides are keyed by DATE and a date's conditions are the
// same whichever tab is open, so they live in one orchestrator-level map. Putting them in
// TabInputs would also break isDualSideInputs above, which discriminates on a `calls` member.
export interface MarketConditionOverride {
    ivCall: number | null;    // FRACTION (0.138 = 13.8%) — dx percent format stores fractions natively
    ivPut: number | null;
    vsCall: number | null;
    vsPut: number | null;
    hsCall: number | null;    // display-only per side; no engine path consumes HS (owner ruling D4, reaffirmed 2026-09-21)
    hsPut: number | null;
    // false = these values were SEEDED from live readings and are merely being displayed.
    // true  = the user edited at least one box, which promotes the WHOLE record to an override
    //         (owner ruling D7). Only edited dates reach the wire.
    edited: boolean;
    // In-flight seed fetches, tracked SEPARATELY because the two land independently: IV comes back
    // from a cached query almost immediately, while vertical skew may have to warm a backend slot
    // for up to ~5s. One shared flag would leave the IV box spinning until the slower call
    // finished, which reads as a hang.
    ivLoading: boolean;
    vsLoading: boolean;
}

export type MarketConditionOverrides = { [dateKey: string]: MarketConditionOverride };

export function makeEmptyOverride(): MarketConditionOverride {
    return {ivCall: null, ivPut: null, vsCall: null, vsPut: null, hsCall: null, hsPut: null,
        edited: false, ivLoading: false, vsLoading: false};
}

// A value copy of a record — the ONE place the field list is spelled out for copying, so a new
// per-side field cannot be silently dropped by one of the many copy sites (both hosts, the
// fieldset, the run-fire snapshot). State flags are copied as given unless overridden.
export function copyOverride(v: MarketConditionOverride,
                             state?: { edited?: boolean; ivLoading?: boolean; vsLoading?: boolean }
): MarketConditionOverride {
    const s = state || {};
    return {
        ivCall: v.ivCall, ivPut: v.ivPut, vsCall: v.vsCall, vsPut: v.vsPut,
        hsCall: v.hsCall, hsPut: v.hsPut,
        edited: s.edited !== undefined ? s.edited : v.edited,
        ivLoading: s.ivLoading !== undefined ? s.ivLoading : v.ivLoading,
        vsLoading: s.vsLoading !== undefined ? s.vsLoading : v.vsLoading
    };
}

// The per-box changed-since-run flags a fieldset paints from (CR-20260831-004). One flag per
// side per value; the fieldset picks its own side's three.
export interface OverrideChangedKeys {
    ivCall: boolean;
    ivPut: boolean;
    hsCall: boolean;
    hsPut: boolean;
    vsCall: boolean;
    vsPut: boolean;
}

// The six diff-key suffixes under 'ovr.<dateKey>.' — the grammar both hosts' diffs write and both
// hosts' changed-key readers consume.
export const OVERRIDE_VALUE_KEYS: (keyof OverrideChangedKeys)[] =
    ['ivCall', 'ivPut', 'hsCall', 'hsPut', 'vsCall', 'vsPut'];

// Read the anchored scope's changed flags out of a flat diff map, unioned over `dateKeys`
// (owner ruling 2026-09-17: on the All-dates position a box lights if ANY date in the range
// no longer matches what the run was sent). Null when nothing changed.
export function readOverrideChangedKeys(map: { [key: string]: boolean } | null | undefined,
                                        dateKeys: string[]): OverrideChangedKeys | null {
    if (!map || dateKeys.length === 0) {
        return null;
    }
    const out: OverrideChangedKeys = {ivCall: false, ivPut: false, hsCall: false, hsPut: false,
        vsCall: false, vsPut: false};
    let any = false;
    for (let i = 0; i < dateKeys.length; i++) {
        const p = 'ovr.' + dateKeys[i] + '.';
        for (let k = 0; k < OVERRIDE_VALUE_KEYS.length; k++) {
            const field = OVERRIDE_VALUE_KEYS[k];
            if (map[p + field]) {
                out[field] = true;
                any = true;
            }
        }
    }
    return any ? out : null;
}

// A SUPPLIED value: present, finite and STRICTLY POSITIVE (owner ruling D22, 2026-07-22 —
// "negative values must be prohibited on all three, zero should be impossible to enter, missing
// also prohibited... if you decide to override values, you must enter them all").
//
// Zero is rejected even for vertical skew, where index 0 is a legitimate FLAT reading. This
// narrows D15 but does not contradict it: D15 forbids AUTO-FILLING a zero (which would silently
// classify as Flat and change the engine's output), and the box still stays EMPTY rather than
// showing a filler. D22 additionally forbids the user TYPING one. Nothing becomes untestable —
// any value 1..14 still lands in the Flat band.
export function isOverrideValueSupplied(v: number | null): boolean {
    return typeof v === 'number' && isFinite(v) && v > 0;
}

// Complete = ALL FOUR values supplied. hs is now REQUIRED (D22: "you must enter them all"),
// superseding D4's exclusion HERE — D4 governs what the wire carries and what the engine reads,
// never whether the user may leave the box empty.
//
// BOTH vertical skews are required even on a single-side tab, where only one has a box. That is
// deliberate: the backend's OpgMarketOverride is complete-by-construction (non-nullable skews, so
// nothing downstream re-checks), and OpgRunNormalizer.BuildOverrides drops any record missing
// either one. Demanding only the visible side here would let a record pass this gate and then be
// silently discarded server-side — reintroducing, one layer down, the exact silent degrade this
// ruling exists to remove. One rule across all three layers is worth more than a tailored one.
//
// The off-side value is not something the user types: it arrives with its sibling in a single
// VerticalSkewChangedDto, and the VS box spins while either side is unresolved. A permanently
// dead off-side feed is therefore the one case that blocks the Run without an on-screen cause —
// rare, and it fails in the safe direction.
//
// The Run gate (opg-copilot.component canRun) and the wire filter (backend-opg-trades-skill
// mapMarketOverrides) BOTH call this, so a record can never be run-able yet silently dropped.
// CONSUME-SCOPED since the owner's 2026-08-04 ruling (CR-20260804-001, at the smoke): "we
// should require only what the run consumes." IV is always consumed; a side's vertical skew
// is required iff THAT side runs (a calls-only tab never reads the put-side skew — demanding
// it locked the Run on a value the single-side tab never displays). HS is required by NOTHING
// (owner ruling D4: carried, never consumed), so it no longer gates either — a supplied HS
// still crosses the wire and still refuses non-positive values there. This supersedes the
// all-four-boxes clause of D22; the whole-record promotion (D7 — editing any box promotes the
// date's record, no per-field fallback for CONSUMED values) stands. The backend validator and
// normalizer apply the SAME side-scoped rule.
//
// CR-20260921-002: IV is per side now, so it joins the skew under the same consume scope — a
// side that runs needs ITS IV and ITS vertical skew; a side that does not run needs nothing.
export function isOverrideComplete(o: MarketConditionOverride, needsCalls: boolean,
                                   needsPuts: boolean): boolean {
    return o != null
        && (!needsCalls || (isOverrideValueSupplied(o.ivCall) && isOverrideValueSupplied(o.vsCall)))
        && (!needsPuts || (isOverrideValueSupplied(o.ivPut) && isOverrideValueSupplied(o.vsPut)));
}

// The AGGREGATE anchor position — "All dates" (client screencast 2026-09-17, card nTGh4JWd).
// Encoded as -1 so it sits immediately before the first real date on the SAME axis both hosts
// already step along: prev/next arithmetic, and the clamp in each host's current-key getter, then
// keep working untouched. The clamp is load-bearing rather than incidental — it makes -1 resolve
// to the FIRST date, which is exactly the date the All position must stream and display.
export const ALL_DATES_INDEX = -1;

// Value equality for the six USER-FACING numbers only. `edited` and the two loading flags are
// record STATE, not values, and are deliberately excluded: an All-dates stamp writes edited=true
// to every date in the same pass, so comparing it would make this predicate trivially true, and
// two dates can legitimately hold identical numbers while one still has a feed in flight.
//
// Null semantics copied from the param-diff's own nullNumEq: NaN counts as empty, and two empties
// are equal. Exact === on the numbers is right here — a stamp COPIES one value onto every date,
// so there is no arithmetic in between to introduce a float delta.
export function overrideValuesEqual(a: MarketConditionOverride | null,
                                    b: MarketConditionOverride | null): boolean {
    if (!a || !b) {
        return !a && !b;
    }
    return numEq(a.ivCall, b.ivCall) && numEq(a.ivPut, b.ivPut)
        && numEq(a.hsCall, b.hsCall) && numEq(a.hsPut, b.hsPut)
        && numEq(a.vsCall, b.vsCall) && numEq(a.vsPut, b.vsPut);
}

function numEq(a: number | null, b: number | null): boolean {
    const aEmpty = a == null || (typeof a === 'number' && isNaN(a));
    const bEmpty = b == null || (typeof b === 'number' && isNaN(b));
    if (aEmpty || bEmpty) {
        return aEmpty === bEmpty;
    }
    return a === b;
}

// Do ALL of `keys` carry the same record? This is the question the "All dates" label must answer
// before it may drop its source-date hint (owner ruling 2026-09-17: "always show the date hint if
// you're showing market values, the user must know where they are coming from").
//
// The bare "All dates" label CLAIMS the numbers on screen are true of every date. That claim is
// earned by PROOF, never assumed — so a key with no record at all is a No, not a skip: that date
// is showing a live market reading rather than the stamped value, which is precisely the case the
// hint exists to disclose. An empty key list is likewise a No; nothing is proven about no dates.
export function isUniformAcrossDates(map: MarketConditionOverrides | null | undefined,
                                     keys: string[]): boolean {
    if (!map || !keys || keys.length === 0) {
        return false;
    }
    const first = map[keys[0]];
    if (!first) {
        return false;
    }
    for (let i = 1; i < keys.length; i++) {
        if (!overrideValuesEqual(first, map[keys[i]] || null)) {
            return false;
        }
    }
    return true;
}

// ---- Skill-authored trade content (Value — rendered verbatim, courier §6) ---------------

// Grid-expressible by construction: mold + legs + width are exactly what a
// PricingGridStrategyDescriptor carries; startingStrike says where it anchors on the
// ladder. DATE-BOUND (amendment A5, supersedes A2): every trade NAMES its own expiration
// (the `expiration` field below — a listed chain key); ranked trades in a Date Range may
// each sit on a DIFFERENT date. Any per-leg
// expirationOffset is business days from the trade's OWN expiration (the grid's
// findExpirationOffset rule; absent/0 = the trade's expiration itself). Call trades read
// UP from startingStrike, put trades DOWN (§7; put-side mirror = §14 assumption pending
// Chris).
export interface OpgTradeContent {
    presetId?: string;
    presetRevision?: number;
    presetName?: string;
    presetProvenance?: string;
    name: string;                       // display name; minted UNIQUE by the mock (addStrategy never de-dupes provided names)
    side: OpgSide;
    expiration: string;                 // the expiration this trade is BOUND to (A5) — a 'yyyy-MM-dd' chain key, one of the request context's expirations
    startingStrike: number;
    mold: StrategyTemplate;
    legs: PricingGridLegDescriptor[];   // per-leg expirationOffset = calendarized from THIS trade's expiration (§2/A5); never an absolute date
    width: number;
    priceAtSelection: number;           // signed credit(+)/debit(-); pre-first-tick fallback ONLY — cards are quote-live (§6)
    maxRiskLabel: string;               // the trade's max-risk VALUE/category (BF-4/A4): mixed-risk "All" runs vary the category per trade; the card adds the "Max Risk" label. Both CR: '' on a follow-through in a Both run — risk levels shape only the range family, and the card hides the line
    score?: number;                     // display-only; nothing structural depends on it (§6, open #2)
    tradeType?: 'range' | 'followThrough';   // Both CR: the family a candidate belongs to. REQUIRED in a mixed (Both) result — the user is comparing kinds, a card must never be ambiguous; absent in single-family runs (their cards stay exactly as before)
    // CR-20260924-002 (ES mode, Hedge Matrix host only): the ES contract the backend lens translated
    // this trade onto ('ESZ6') — startingStrike is then the ES strike and every leg qty the ES count —
    // and the fair-value gap it used. Absent on an SPX trade. The card builds its tickers on that
    // contract's chain; a preview creates a missing ES silo on that gap.
    esContract?: string | null;
    esGap?: number | null;
}

// ---- Suggestions (Entity: host identity + mutable state around the Value) ---------------

export interface OpgTradeSuggestion {
    id: string;                          // host-minted "<runId>#<side>#<index>" — NEVER the name
    content: OpgTradeContent;
    state: { previewed: boolean };
}

// A side result is a LIST of member suggestions (see SideResult). The set-level "fully
// previewed" state (the Load-All bar flips to Un-Preview All) is DERIVED from the members,
// never stored — members carry their own previewed flags.
export function allMembersPreviewed(members: OpgTradeSuggestion[]): boolean {
    return members != null && members.length > 0 && members.every(x => x.state.previewed);
}

export function unPreviewedMembers(members: OpgTradeSuggestion[]): OpgTradeSuggestion[] {
    return members == null ? [] : members.filter(x => !x.state.previewed);
}

// WI-7 Load-All gate (pure): a single-date OPG grid physically cannot hold more than one
// expiration, so the Load-All bar may fire ONLY while the UN-PREVIEWED members share one bound
// date. distinctUnpreviewedExpirationCount counts the distinct expirations still to load (0 when
// none remain); loadAllGateBlocked is true when that exceeds 1 — the bar disables (with a reason)
// and the set is enumerated card-by-card instead, each preview navigating (WI-6). Both read the
// LIVE previewed flags, so as the user hand-previews the odd-date members the remaining set can
// shrink to one date and the gate re-opens. ES5: arrays + indexOf only (no Set).
export function distinctUnpreviewedExpirationCount(members: OpgTradeSuggestion[]): number {
    const remaining = unPreviewedMembers(members);
    const dates: string[] = [];
    for (let i = 0; i < remaining.length; i++) {
        const exp = remaining[i] && remaining[i].content ? remaining[i].content.expiration : null;
        if (exp && dates.indexOf(exp) === -1) {
            dates.push(exp);
        }
    }
    return dates.length;
}

// Blocked in the LOAD direction only — the flip to Un-Preview All (fully previewed) is always
// allowed — and only when the remaining members span more than one expiration.
export function loadAllGateBlocked(members: OpgTradeSuggestion[]): boolean {
    return !allMembersPreviewed(members) && distinctUnpreviewedExpirationCount(members) > 1;
}

// ---- Grid-state fingerprint (WI-15 / amendment A6) ---------------------------------------

// The stale-grid indicator's comparands: EXACTLY the grid reads the CoPilot consumes and then
// freezes into its answers, and which the USER can change under it while the conversation lives:
//   expiration — the anchor a 'loaded' date resolves to and the date every trade is bound to
//   chosenAtm  — the ATM-Offset anchor (the panel's chosen/override ATM, the black cell)
//   defaultQty — the quantity every loaded trade inherits (owner: "include default qty")
//
// The UNDERLYING is deliberately NOT a member (owner ruling 2026-07-14, on re-asking): a symbol
// change RESETS the conversation, exactly as it always has. The baseline dies with it, so the
// symbol can never differ from what was captured — a 'symbol' member could not fire even once.
//
// Also NOT members: the market/dynamic ATM, the strike ladder, the expiration chain and quote
// ticks. Those move on their own; yellow must mean "the USER moved the grid under me", never
// "the market moved" (the HedgeMatrix donor's rule).
export interface OpgGridFingerprint {
    expiration: string | null;   // chain key 'yyyy-MM-dd'; null = the panel has no expiration yet
    chosenAtm: number | null;    // null = not a finite number (the field is nullable in the host)
    defaultQty: number | null;   // null = not a finite number (ditto)
}

export type OpgGridMember = 'expiration' | 'chosenAtm' | 'defaultQty';

// Normalizing constructor. The host fields behind these reads are nullable despite their
// `: number` signatures (centerStrike is nulled on a symbol switch and back-filled from the
// market later; defaultQty is nulled when no user setting exists), so every raw read is coerced
// to one canonical "unknown" — otherwise the same logical state would fingerprint differently as
// the value flipped between null / undefined / NaN.
export function makeGridFingerprint(
    expiration: string | null | undefined,
    chosenAtm: number | null | undefined,
    defaultQty: number | null | undefined
): OpgGridFingerprint {
    return {
        expiration: (typeof expiration === 'string' && expiration.length > 0) ? expiration : null,
        chosenAtm: finiteOrNull(chosenAtm),
        defaultQty: finiteOrNull(defaultQty)
    };
}

// The members whose CAPTURED value differs from the LIVE one — the drift set that lights the yellow.
// A null / '' captured member is compared like any other VALUE, because for these members null is a
// real state of the panel, not an absence: an EMPTY Default Qty box (no saved setting for this
// underlying) is the normal first-visit state, and the user typing 10 into it IS a grid change the
// CoPilot must report. The genuine "not loaded yet" case is handled ONCE, at the baseline, by
// adoptPreLoadBaseline — never by ignoring nulls here, which would make the FIRST edit of any unset
// member invisible forever (the poke that carries a user's edit is the same poke that would adopt it).
export function driftedGridMembers(
    captured: OpgGridFingerprint | null,
    live: OpgGridFingerprint | null
): OpgGridMember[] {
    const drifted: OpgGridMember[] = [];
    if (!captured || !live) {
        return drifted;   // no baseline yet (no conversation) => nothing can be stale
    }
    if (captured.expiration !== live.expiration) {
        drifted.push('expiration');
    }
    if (captured.chosenAtm !== live.chosenAtm) {
        drifted.push('chosenAtm');
    }
    if (captured.defaultQty !== live.defaultQty) {
        drifted.push('defaultQty');
    }
    return drifted;
}

// Drop the members that are drifting but IRRELEVANT to what the CoPilot would actually say.
//
// chosenAtm is only a comparand when a questionnaire READS it — i.e. some side's Price Range is on
// ATM Offset, where the chosen ATM is the anchor the region is measured from (owner ruling
// 2026-07-14: "only mark it yellow if Price Range is selected as ATM Offset, otherwise we don't care
// if the Center ATM value of the OPG grid changed"). The other two Price Range forms name their
// strikes outright — an explicit start/end pair, or a follow-through from a picked strike — so the
// panel's centre strike is nothing the answers depend on, and moving it must not nag.
export function relevantGridMembers(drifted: OpgGridMember[], chosenAtmInUse: boolean): OpgGridMember[] {
    if (chosenAtmInUse || !drifted) {
        return drifted;
    }
    return drifted.filter(m => m !== 'chosenAtm');
}

// Does THIS side's questionnaire read the panel's chosen ATM? Only the ATM Offset form does — it
// anchors its region on it (the anchor pill, WI-4). Note it counts even when the side is anchored on
// MARKET ATM: the Chosen ATM pill still renders (and still shows the chosen strike) in that mode, so
// a chosen-ATM the user has moved under us is still on screen and still capable of lying.
export function sideUsesAtmOffset(side: SideInputs): boolean {
    return !!side && !!side.priceRange && side.priceRange.kind === 'atmOffset';
}

// CR-20260727-001. Does THIS side's questionnaire depend on the skill's expected-range boundary?
// TRUE for BOTH entry points, deliberately: the top-level 'expectedRange' form, and an ATM Offset
// whose bounded sub is sized by the expected range ("Use Expected Range"). They reduce to the same
// backend boundary math, so any rule about expected-range availability must see both — checking
// only the top-level kind would leave the ATM Offset route silently exempt.
export function sideUsesExpectedRange(side: SideInputs): boolean {
    if (side && side.mode === 'preset') { return false; }
    const pr = !!side ? side.priceRange : null;
    if (!pr) {
        return false;
    }
    if (pr.kind === 'expectedRange') {
        return true;
    }
    return pr.kind === 'atmOffset' && !!pr.sub && pr.sub.kind !== 'followThrough'
        && !!pr.sub.value && pr.sub.value.mode === 'expectedRange';
}

// A baseline taken before the panel had built its grid — no expiration chain, or no centre strike
// yet. Its values are meaningless, so the real ones arriving later are a LOAD, not a user edit.
//
// chosenAtm counts as a readiness signal (defaultQty deliberately does NOT): the panel ALWAYS
// back-fills centerStrike from the market the first time it builds its rows, so a null chosen ATM
// means the grid has not built them yet. A null Default Qty means only that this underlying has no
// saved quantity — a stable, ordinary state of a fully-loaded panel, and the user's first edit of it
// must read as drift.
export function isPreLoadBaseline(captured: OpgGridFingerprint): boolean {
    return !captured
        || captured.expiration === null
        || captured.chosenAtm === null;
}

// Startup guard (the donor's adoptEmptyBaselineIfNeeded): upgrade a PRE-LOAD baseline to the panel's
// first real reads, so the chain finishing loading never reads as a user edit (no yellow flash at
// first open, and none while a symbol switch settles — the fresh conversation is started mid-restore,
// before the panel has re-derived its expiration and ATM). Deliberately all-or-nothing on the
// pre-load test rather than per-null-member: once the panel HAS an expiration and an ATM, every
// member's value — including a null Default Qty — is a genuine reading of the grid, and adopting one
// later would swallow the user's first edit of it. Self-terminating: adopting fills the expiration,
// so the baseline stops being pre-load. Returns the SAME reference when there is nothing to adopt,
// so callers can tell cheaply whether it moved.
export function adoptPreLoadBaseline(
    captured: OpgGridFingerprint,
    live: OpgGridFingerprint
): OpgGridFingerprint {
    if (!captured || !live || !isPreLoadBaseline(captured)) {
        return captured;
    }
    if (isPreLoadBaseline(live)) {
        return captured;   // the panel is not ready either — stay pre-load, adopt nothing yet
    }
    return {
        expiration: live.expiration,
        chosenAtm: live.chosenAtm,
        defaultQty: live.defaultQty
    };
}

// Re-baseline ONE member (WI-6 navigation): the CoPilot's own expiration write must not read as
// user drift, but a drift the user made to the ATM / qty must SURVIVE our navigation — so the
// expiration member is replaced and the rest is carried over untouched. (A whole-fingerprint
// re-capture here would silently absorb — and hide — a concurrent user edit.)
export function withExpiration(captured: OpgGridFingerprint, expiration: string | null): OpgGridFingerprint {
    return makeGridFingerprint(expiration, captured.chosenAtm, captured.defaultQty);
}

function finiteOrNull(v: number | null | undefined): number | null {
    return (typeof v === 'number' && isFinite(v)) ? v : null;
}

// ---- Run model (§6, §11) ----------------------------------------------------------------

// 'empty' is a valid "no suggestion", NOT a failure; 'terminalFailed' covers throw/stall
// (the ~45 s backstop) — stuck ≠ empty (§11). 'cancelled'/'preSendBlocked' kept for
// phase-2 parity with the Hedge union (the mock never emits them).
export type RunOutcome =
    | 'pending'
    | 'succeeded'
    | 'empty'
    | 'cancelled'
    | 'terminalFailed'
    | 'preSendBlocked';

// Per-side raw contents a settled Run carries — ONE ranked LIST of trades (index 0 =
// best), whose dates may differ in a Date Range (FR-14: no result-shape discriminator).
// (The skill wire shape lives in opg-trades-skill.ts; the orchestrator maps it to this
// domain shape before completeRun.)
export type SideTradeContents = { trades: OpgTradeContent[] };

export interface RunContents {
    calls?: SideTradeContents;
    puts?: SideTradeContents;
    // CR-20260902-001: the skill's explanation for a side that came back EMPTY (one line per
    // affected date, '\n'-joined) — e.g. an Expected Range whose top lies at/below the start
    // strike. Absent = nothing beyond the generic "no trades" message.
    callsNote?: string;
    putsNote?: string;
}

// Built suggestions per side (from RunContents on 'succeeded'): the member suggestions,
// rendered as ONE ranked card stack plus the gated Load-All bar (the gating is WI-7).
export interface SideResult {
    members: OpgTradeSuggestion[];
}

export interface RunResults {
    calls?: SideResult;
    puts?: SideResult;
}

export interface Run {
    id: string;                 // client-minted per Run — the §11 correlation key (run log + click-trail)
    tabKind: TabKind;
    sentInputs: TabInputs;      // snapshot of what was sent (verbatim courier, §6)
    outcome: RunOutcome;
    startedAt: number;          // epoch ms
    endedAt: number | null;
    failureDetail: string | null;
    // CR-20260902-001: per-side empty explanations from RunContents, kept on the settled run so
    // the results list can print WHY a side (or the whole run) has no trades. Null = none.
    callsNote: string | null;
    putsNote: string | null;
}

// ---- Conversation aggregate -------------------------------------------------------------

export interface Tab {
    kind: TabKind;
    inputs: TabInputs | null;
    currentRun: Run | null;     // <=1 CURRENT Run per tab; a new Run supersedes
    results: RunResults | null; // from currentRun when 'succeeded'
}

export interface Conversation {
    id: string;
    symbol: string;             // the underlying the session was opened against (§8: symbol change resets)
    tabs: { calls: Tab; puts: Tab; callsAndPuts: Tab };
}

// ---- Loaded-trade registry entry (§9 identity spine) --------------------------------------

// One row per PREVIEWED trade: links the card (suggestionId), the run that produced it
// (runId — §11 trace), the real grid strategy (strategyId — minted by the host at load),
// and the border key parts (colId derives from strategyId+side; strike below). Carries the
// trade's bound `expiration` (A5) — WI-6 (preview navigation) and WI-7 (Load-All gating)
// consult it to enumerate/switch dates. tabKind scopes re-Run cleanup to exactly that
// tab's previews (§7) even across superseded runs.
export interface CopilotLoadedTrade {
    suggestionId: string;
    runId: string;
    tabKind: TabKind;
    strategyId: string;
    side: OpgSide;
    startingStrike: number;
    expiration: string;
}
